import {
  createFileRoute,
  redirect,
  useLoaderData,
  useRouter,
} from "@tanstack/react-router";
import { useSetAtom } from "jotai";
import { useEffect, useState, type JSX } from "react";
import { z } from "zod";

import { graphql } from "@repo/graphql-types/gql";

import {
  type RecurrenceScope,
  RecurrenceScopeChooser,
} from "@/components/recurring-appointment/recurrence-scope-chooser";
import { fetchQuery } from "@/hooks/use-graphql";
import {
  recurringAppointmentFormAtom,
  recurringPatientAtom,
} from "@/lib/atoms/recurring-appointment-form-atom";
import { rescheduleRecurrenceAtom } from "@/lib/atoms/reschedule-recurrence-form-atom";
import {
  capitalizeMonth,
  convertDateTimeIgnoringTimezone,
  formatIgnoringTimezone,
  removeSecondsFromTimeString,
} from "@/lib/helpers/date";

const ROUTE_ID = "/appointment/$appointmentId/reschedule-recurrence";

const RescheduleRecurrenceScopeQuery = graphql(/* GraphQL */ `
  query RescheduleRecurrenceScopeQuery($codAgendamento: Int!) {
    agendamento: agendamentos_by_pk(codAgendamento: $codAgendamento) {
      codAgendamento
      codAgendamentoRecorrente
      data
      horaInicio
      codUnidade
      codUsuarioCompromisso
      codUsuarioFormaRecebimento
      Unidade {
        nomeLimpo
        sigla
      }
      UsuarioCompromisso {
        nome
      }
      Paciente {
        codPaciente
        nome
        telefone
        telefoneInternacionalNormalizado
        email
      }
    }
  }
`);

const RescheduleRecurrenceBlockQuery = graphql(/* GraphQL */ `
  query RescheduleRecurrenceBlockQuery($codAgendamentoRecorrente: Int!) {
    agendamentosRecorrentes(
      where: { codAgendamentoRecorrente: { eq: $codAgendamentoRecorrente } }
    ) {
      codAgendamentoRecorrente
      codUnidade
      frequenciaEmDias
      agendamentos {
        codAgendamento
        data
        horaInicio
        cancelado
      }
    }
  }
`);

const RescheduleRecurrenceUnitsQuery = graphql(/* GraphQL */ `
  query RescheduleRecurrenceUnitsQuery($codAgendamentoRecorrente: Int!) {
    agendamentos(
      where: { codAgendamentoRecorrente: { _eq: $codAgendamentoRecorrente } }
    ) {
      codAgendamento
      codUnidade
      Unidade {
        nomeLimpo
        sigla
      }
    }
  }
`);

/** Normaliza uma data (date ou DateTime) para "yyyy-MM-dd" ignorando timezone. */
const toDateString = (value: string): string =>
  formatIgnoringTimezone(convertDateTimeIgnoringTimezone(value));

const RescheduleRecurrencePage = (): JSX.Element => {
  const { appointment, blockUnit, sessions, frequency, earliestDate } = useLoaderData({
    from: ROUTE_ID,
  });
  const router = useRouter();

  const setForm = useSetAtom(recurringAppointmentFormAtom);
  const setPatient = useSetAtom(recurringPatientAtom);
  const setRescheduleContext = useSetAtom(rescheduleRecurrenceAtom);

  const [scope, setScope] = useState<RecurrenceScope>("single");

  const codAgendamento = appointment.codAgendamento;
  const patient = appointment.Paciente;

  useEffect(() => {
    setForm({
      unit: blockUnit.codUnidade,
      appointmentType: appointment.codUsuarioCompromisso,
      paymentMethod: appointment.codUsuarioFormaRecebimento,
      frequency,
      startDate: convertDateTimeIgnoringTimezone(earliestDate),
      unitName: blockUnit.nome,
      unitAcronym: blockUnit.sigla,
      appointmentTypeName: appointment.UsuarioCompromisso?.nome ?? "",
    });

    setPatient({
      patientId: patient?.codPaciente,
      telefoneInternacionalNormalizado: patient?.telefoneInternacionalNormalizado ?? null,
      phone: patient?.telefone ?? "",
      phoneCountry: undefined,
      name: patient?.nome ?? "",
      dateOfBirth: undefined,
      email: patient?.email ?? "",
      cpf: undefined,
      sendConfirmationViaWhatsApp: true,
      roomTypes: [],
    });

    setRescheduleContext({
      codAgendamentoRecorrente: appointment.codAgendamentoRecorrente ?? 0,
      oldSessions: sessions,
    });
  }, [
    appointment,
    blockUnit,
    patient,
    sessions,
    frequency,
    earliestDate,
    setForm,
    setPatient,
    setRescheduleContext,
  ]);

  const dateObject = convertDateTimeIgnoringTimezone(appointment.data);
  const dayAndMonth = capitalizeMonth(formatIgnoringTimezone(dateObject, "d 'de' MMMM"));
  const startTime = removeSecondsFromTimeString(appointment.horaInicio);

  const handleBack = (): void => {
    router.history.back();
  };

  const handleContinue = (): void => {
    if (scope === "all") {
      void router.navigate({ to: "/recurring-appointments/reschedule/availability" });
      return;
    }

    void router.navigate({
      to: "/appointment/$appointmentId/reschedule",
      params: { appointmentId: codAgendamento },
    });
  };

  return (
    <RecurrenceScopeChooser
      title="Reagendar"
      subtitle="Escolha se deseja alterar apenas este agendamento ou todos os seguintes"
      singleLabel="Este agendamento"
      singleDescription={`${dayAndMonth} · ${startTime}`}
      allLabel="Este e os próximos agendamentos"
      allDescription={`A partir de ${dayAndMonth}`}
      value={scope}
      onChange={setScope}
      onContinue={handleContinue}
      onBack={handleBack}
    />
  );
};

export const Route = createFileRoute("/appointment/$appointmentId/reschedule-recurrence")(
  {
    component: RescheduleRecurrencePage,
    parseParams: (params) => ({
      appointmentId: z.int().parse(Number(params.appointmentId)),
    }),
    loader: async (opts) => {
      const data = await fetchQuery(opts.context, RescheduleRecurrenceScopeQuery, {
        codAgendamento: opts.params.appointmentId,
      });

      const appointment = data.agendamento;

      if (!appointment || appointment.codAgendamentoRecorrente == null) {
        throw redirect({ to: "/calendar" });
      }

      const blockData = await fetchQuery(opts.context, RescheduleRecurrenceBlockQuery, {
        codAgendamentoRecorrente: appointment.codAgendamentoRecorrente,
      });

      const block = blockData.agendamentosRecorrentes?.[0];

      if (!block) {
        throw redirect({ to: "/calendar" });
      }

      const unitsData = await fetchQuery(opts.context, RescheduleRecurrenceUnitsQuery, {
        codAgendamentoRecorrente: appointment.codAgendamentoRecorrente,
      });

      const unitByAppointment = new Map(
        (unitsData.agendamentos ?? []).map((item) => [
          item.codAgendamento,
          item.codUnidade,
        ]),
      );

      const codUnidadeBloco = block.codUnidade ?? appointment.codUnidade;
      const naUnidadeDoBloco = (unitsData.agendamentos ?? []).find(
        (item) => item.codUnidade === codUnidadeBloco,
      );
      const blockUnit = {
        codUnidade: codUnidadeBloco,
        nome:
          naUnidadeDoBloco?.Unidade?.nomeLimpo ?? appointment.Unidade?.nomeLimpo ?? "",
        sigla: naUnidadeDoBloco?.Unidade?.sigla ?? appointment.Unidade?.sigla ?? "",
      };

      const tappedDate = toDateString(appointment.data);

      const sessions = block.agendamentos
        .filter(
          (session) => !session.cancelado && toDateString(session.data) >= tappedDate,
        )
        .map((session) => {
          const codUnidade = unitByAppointment.get(session.codAgendamento);

          return {
            codAgendamento: session.codAgendamento,
            data: toDateString(session.data),
            horaInicio: session.horaInicio,
            ...(codUnidade != null && codUnidade !== blockUnit.codUnidade
              ? { codUnidade }
              : {}),
          };
        })
        .sort((a, b) => a.data.localeCompare(b.data));

      const earliestDate = sessions[0]?.data ?? tappedDate;

      return {
        appointment,
        blockUnit,
        sessions,
        frequency: block.frequenciaEmDias,
        earliestDate,
      };
    },
  },
);
